/** Réponse devis / quittance (aligné app mobile) */
export interface ApiQuittance {
  CODEINTE?: number | string;
  NUMEPOLI?: string;
  NUMEQUIT?: string;
  FORMCOUV?: string;
  CODECATE?: number | string;
  CODEASSU?: number | string;
  LIBFORCO?: string;
  PRIMNETT?: number | string;
  TAXEPRIM?: number | string;
  PRIMTOTA?: number | string;
  PRIM_TTC?: string;
  DATEEFFE?: string;
  DATEECHE?: string;
  CAPIASSU?: number | string;
  [key: string]: unknown;
}

export interface ApiGarantie {
  CODEGARA?: string;
  LIBEGARA?: string;
  CAPIASSU?: number | string;
  capiassu?: number | string;
  [key: string]: unknown;
}

/** Valeurs de rachat (réponse devis / AFN, aligné mobile Rachat) */
export interface ApiRachat {
  AN?: string | number;
  annee?: string | number;
  'LIBE__AN'?: string;
  libelleAnnee?: string;
  VR?: number | string;
  vr?: number | string;
  VC?: number | string;
  vc?: number | string;
  VR_F?: string;
  VRF?: number | string;
  VC_F?: string;
  VCF?: number | string;
  [key: string]: unknown;
}

export interface ApiDetailQuittance {
  id?: string;
  logoSuffix?: string;
  quittance?: ApiQuittance | null;
  garanties?: ApiGarantie[] | null;
  rachats?: ApiRachat[] | null;
  [key: string]: unknown;
}

/**
 * Payload `selectedQuittance` aligné mobile `DetailQuittance.toJson()` :
 * uniquement quittance + garanties (pas de rachats ni de champs extra).
 * Le backend Bora/Millénium fait `mapper.convertValue(..., DetailQuittance.class)`
 * avec un ObjectMapper strict.
 */
export function serializeDetailQuittanceForAfn(
  detail: ApiDetailQuittance | null | undefined,
  fallbackFormcouv?: string,
): Record<string, unknown> | null {
  const quittance = detail?.quittance;
  const formcouv =
    toOptionalString(quittance?.FORMCOUV) ||
    toOptionalString(quittance?.['fromcouv']) ||
    toOptionalString(quittance?.['formcouv']) ||
    toOptionalString(fallbackFormcouv);

  if (!quittance && !formcouv) {
    return null;
  }

  const serializedQuittance = compactRecord(
    quittance ? serializeQuittanceForAfn(quittance) : {},
  );
  if (formcouv) {
    serializedQuittance['FORMCOUV'] = formcouv;
  }

  return {
    quittance: serializedQuittance,
    garanties: (detail?.garanties ?? []).map((garantie) => compactRecord(serializeGarantieForAfn(garantie))),
  };
}

function serializeQuittanceForAfn(quittance: ApiQuittance): Record<string, unknown> {
  return {
    CODEINTE: toOptionalNumber(quittance.CODEINTE),
    NUMEPOLI: toOptionalString(quittance.NUMEPOLI),
    NUMEQUIT: toOptionalString(quittance.NUMEQUIT),
    FORMCOUV: toOptionalString(quittance.FORMCOUV) || toOptionalString(quittance['fromcouv']),
    CODECATE: toOptionalNumber(quittance.CODECATE),
    CODEASSU: toOptionalNumber(quittance.CODEASSU),
    NUMEAVEN: toOptionalNumber(quittance['NUMEAVEN']),
    DATECOMP: toOptionalString(quittance['DATECOMP']),
    DATEEMIS: toOptionalString(quittance['DATEEMIS']),
    DATEEFFE: toOptionalString(quittance.DATEEFFE),
    DATEECHE: toOptionalString(quittance.DATEECHE),
    CODEDURE: toOptionalString(quittance['CODEDURE']),
    VALEDURE: toOptionalNumber(quittance['VALEDURE']),
    UNITDURE: toOptionalString(quittance['UNITDURE']),
    LIBVALDU: toOptionalString(quittance['LIBVALDU']),
    LIBFORCO: toOptionalString(quittance.LIBFORCO),
    PRIMNETT: toOptionalNumber(quittance.PRIMNETT),
    TAXEPRIM: toOptionalNumber(quittance.TAXEPRIM),
    ACCEQUIT: toOptionalNumber(quittance['ACCEQUIT']),
    TAXEACCE: toOptionalNumber(quittance['TAXEACCE']),
    PRIMTOTA: toOptionalNumber(quittance.PRIMTOTA),
    PRIM_TTC: toOptionalString(quittance.PRIM_TTC),
    PRIMTOTA_DE: toOptionalNumber(quittance['PRIMTOTA_DE']),
    PRIMTOTA_LDE: toOptionalString(quittance['PRIMTOTA_LDE']),
  };
}

function compactRecord(record: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record)) {
    if (value != null && value !== '') {
      out[key] = value;
    }
  }
  return out;
}

function serializeGarantieForAfn(garantie: ApiGarantie): Record<string, unknown> {
  return {
    CODEGARA: toOptionalString(garantie.CODEGARA),
    LIBEGARA: toOptionalString(garantie.LIBEGARA),
    CAPIASSU: Math.trunc(toOptionalNumber(garantie.CAPIASSU ?? garantie.capiassu) ?? 0),
    'LIBE__CA': toOptionalString(garantie['LIBE__CA']),
    MONTGARA: toOptionalNumber(garantie['MONTGARA']) ?? 0,
    MONTANNU: toOptionalNumber(garantie['MONTANNU']) ?? 0,
    MONTPERI: toOptionalString(garantie['MONTPERI']),
  };
}

function toOptionalString(value: unknown): string | null {
  if (value == null || value === '') {
    return null;
  }
  return String(value);
}

function toOptionalNumber(value: unknown): number | null {
  if (value == null || value === '') {
    return null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const raw = String(value)
    .replace(/[\s\u00A0\u202F\u2007\u2009\u200A\u2008]/g, '')
    .replace(',', '.')
    .trim();
  if (!raw) {
    return null;
  }
  const num = Number(raw);
  return Number.isFinite(num) ? num : null;
}
